import type { SpanExporter, ReadableSpan } from '@opentelemetry/sdk-trace-base';
import { ExportResultCode } from '@opentelemetry/core';
import type { ExportResult } from '@opentelemetry/core';
import type { AttributeValue } from '@opentelemetry/api';
export const LOCAL_OTLP_ENDPOINT = '/otlp/v1/traces';

export interface HttpClient {
  post(url: string, options: { data: unknown }): Promise<unknown>;
}

function hrTimeToNanoString(hrTime: [number, number]): string {
  return String(BigInt(hrTime[0]) * 1_000_000_000n + BigInt(hrTime[1]));
}

function toOtlpAttributeValue(value: AttributeValue): object {
  if (typeof value === 'string') {
    return { stringValue: value };
  }
  if (typeof value === 'boolean') {
    return { boolValue: value };
  }
  if (typeof value === 'number') {
    return Number.isInteger(value)
      ? { intValue: String(value) }
      : { doubleValue: value };
  }
  if (typeof value === 'bigint') {
    return { intValue: String(value) };
  }
  if (Array.isArray(value)) {
    return {
      arrayValue: {
        values: value.map((v) => ({
          value: toOtlpAttributeValue(v as AttributeValue),
        })),
      },
    };
  }
  return { stringValue: String(value) };
}

function toOtlpAttributes(
  attributes: Record<string, AttributeValue | undefined>
): Array<{ key: string; value: object }> {
  return Object.entries(attributes)
    .filter(([, v]) => v !== undefined)
    .map(([key, value]) => ({
      key,
      value: toOtlpAttributeValue(value as AttributeValue),
    }));
}

export class OtlpExporter implements SpanExporter {
  private readonly _endpoint: string;
  private readonly _httpClient: HttpClient | null;

  constructor(
    endpoint: string = LOCAL_OTLP_ENDPOINT,
    { httpClient }: { httpClient?: HttpClient } = {}
  ) {
    this._endpoint = endpoint;
    this._httpClient = httpClient ?? null;
  }

  export(
    spans: ReadableSpan[],
    resultCallback: (result: ExportResult) => void
  ): void {
    const resourceSpans = this._groupByResource(spans);
    const body = { resourceSpans };

    const request = this._httpClient
      ? this._httpClient.post(this._endpoint, { data: body })
      : fetch(this._endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          keepalive: true,
        }).then((response) => {
          if (!response.ok) {
            throw new Error(`Export failed with status ${response.status}`);
          }
        });

    request
      .then(() => resultCallback({ code: ExportResultCode.SUCCESS }))
      .catch((err: unknown) =>
        resultCallback({
          code: ExportResultCode.FAILED,
          error: err instanceof Error ? err : new Error(String(err)),
        })
      );
  }

  shutdown(): Promise<void> {
    return Promise.resolve();
  }

  private _groupByResource(spans: ReadableSpan[]): object[] {
    const resourceMap = new Map<object, ReadableSpan[]>();

    for (const span of spans) {
      const resource = span.resource;
      if (!resourceMap.has(resource)) {
        resourceMap.set(resource, []);
      }
      resourceMap.get(resource)!.push(span);
    }

    return Array.from(resourceMap.entries()).map(
      ([resource, resourceSpans]) => ({
        resource: {
          attributes: toOtlpAttributes(
            (
              resource as {
                attributes: Record<string, AttributeValue | undefined>;
              }
            ).attributes
          ),
        },
        scopeSpans: this._groupByScope(resourceSpans),
      })
    );
  }

  private _groupByScope(spans: ReadableSpan[]): object[] {
    const scopeMap = new Map<string, ReadableSpan[]>();

    for (const span of spans) {
      const key = `${span.instrumentationScope.name}@${
        span.instrumentationScope.version ?? ''
      }`;
      if (!scopeMap.has(key)) {
        scopeMap.set(key, []);
      }
      scopeMap.get(key)!.push(span);
    }

    return Array.from(scopeMap.entries()).map(([, scopeSpans]) => {
      const scope = scopeSpans[0].instrumentationScope;
      return {
        scope: { name: scope.name, version: scope.version ?? '' },
        spans: scopeSpans.map((span) => this._toOtlpSpan(span)),
      };
    });
  }

  private _toOtlpSpan(span: ReadableSpan): object {
    const ctx = span.spanContext();
    return {
      traceId: ctx.traceId,
      spanId: ctx.spanId,
      parentSpanId: span.parentSpanContext?.spanId,
      name: span.name,
      kind: span.kind + 1,
      startTimeUnixNano: hrTimeToNanoString(span.startTime),
      endTimeUnixNano: hrTimeToNanoString(span.endTime),
      attributes: toOtlpAttributes(
        span.attributes as Record<string, AttributeValue | undefined>
      ),
      status: {
        code: span.status.code,
        message: span.status.message,
      },
      droppedAttributesCount: span.droppedAttributesCount,
      droppedEventsCount: span.droppedEventsCount,
      droppedLinksCount: span.droppedLinksCount,
    };
  }
}
